<template>
  <footer class="footer">
    <div class="container footer-inner">
      <div class="footer-col">
        <div class="footer-title">CONTENT</div>
        <router-link class="footer-link" to="/about">About Us</router-link>
        <router-link class="footer-link" to="/treatments">Treatments</router-link>
        <a href="#" class="footer-link" @click.prevent>Doctors</a>
        <router-link class="footer-link" to="/blog">Blog</router-link>
        <router-link class="footer-link" to="/news">News</router-link>
        <router-link class="footer-link" to="/pricing">Pricing</router-link>
        <router-link class="footer-link" to="/faq">FAQ</router-link>
        <router-link class="footer-link" to="/contact">Contact Us</router-link>
      </div>

      <div class="footer-col">
        <div class="footer-title">CONTACT</div>
        <div v-if="contact.address" class="footer-contact-item">
          <svg viewBox="0 0 24 24" width="14" height="14" class="footer-icon" fill="currentColor">
            <path
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
            />
          </svg>
          <span class="footer-contact-text">{{ contact.address }}</span>
        </div>
        <div v-if="contact.phone" class="footer-contact-item">
          <svg viewBox="0 0 24 24" width="14" height="14" class="footer-icon" fill="currentColor">
            <path
              d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.07 21 3 13.93 3 5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.24 1.01l-2.2 2.2Z"
            />
          </svg>
          <a class="footer-contact-text footer-contact-link" :href="'tel:' + phoneTelHref">{{ contact.phone }}</a>
        </div>
        <div v-if="contact.email" class="footer-contact-item">
          <svg viewBox="0 0 24 24" width="14" height="14" class="footer-icon" fill="currentColor">
            <path
              d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
            />
          </svg>
          <a class="footer-contact-text footer-contact-link" :href="'mailto:' + contact.email">{{ contact.email }}</a>
        </div>
      </div>

      <div class="footer-col">
        <div class="footer-title">BUSINESS HOURS</div>
        <div v-for="(line, idx) in businessHourLines" :key="'hours-' + idx" class="footer-hours-item">
          {{ line }}
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="container footer-bottom-inner">
        <div class="footer-brand-wrap">
          <img class="footer-foot-logo" :src="footLogo" alt="DOSE Aesthetics" />
        </div>
        <div class="footer-copy">© {{ year }} DOSE Aesthetics | All Rights Reserved</div>
      </div>
    </div>
  </footer>
</template>

<script>
import { fetchPublicContact } from '../api/contact'
import { fetchPublicWorkHours } from '../api/workHours'

const FALLBACK_HOUR_LINES = [
  'Monday: Closed',
  'Tuesday-Thursday: 9:00 AM - 7:30 PM',
  'Friday: 9:00 AM - 8:00 PM',
  'Saturday: 9:00 AM - 6:00 PM',
  'Sunday: 10:00 AM - 5:00 PM',
]

export default {
  name: 'FooterBar',
  data() {
    return {
      year: new Date().getFullYear(),
      footLogo: require('../assets/images/footLogo.png'),
      contact: {
        address: '',
        phone: '',
        email: '',
      },
      businessHourLines: [...FALLBACK_HOUR_LINES],
    }
  },
  computed: {
    phoneTelHref() {
      return String(this.contact.phone || '').replace(/\s+/g, '')
    },
  },
  created() {
    fetchPublicContact()
      .then((data) => {
        this.contact = {
          address: (data.address || '').trim(),
          phone: (data.phone || '').trim(),
          email: (data.email || '').trim(),
        }
      })
      .catch(() => {
        /* 接口失败时 CONTACT 区块可为空 */
      })

    fetchPublicWorkHours()
      .then((list) => {
        const lines = buildBusinessHourLines(list)
        if (lines.length) {
          this.businessHourLines = lines
        }
      })
      .catch(() => {
        /* 失败时保留 FALLBACK_HOUR_LINES */
      })
  },
}

/** 单日展示文案：休息为 Closed，否则用 hoursDisplay 或由开门/关门时间拼接 */
function dayHoursText(row) {
  if (row.isRest === 1 || row.isRest === true) {
    return 'Closed'
  }
  const display = (row.hoursDisplay || row.hours_display || '').trim()
  if (display) {
    return display
  }
  const open = formatTime12h(row.openTime || row.open_time)
  const close = formatTime12h(row.closeTime || row.close_time)
  if (open && close) {
    return `${open} - ${close}`
  }
  return ''
}

function formatTime12h(time24) {
  if (!time24) return ''
  const parts = String(time24).trim().split(':')
  const h = parseInt(parts[0], 10)
  const m = parseInt(parts[1] || '0', 10)
  if (Number.isNaN(h)) return ''
  const suffix = h >= 12 ? 'PM' : 'AM'
  let hour12 = h % 12
  if (hour12 === 0) hour12 = 12
  if (m === 0) return `${hour12}:00 ${suffix}`
  return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`
}

/** 连续且展示文案相同的日期合并为 Tuesday-Thursday 形式 */
function buildBusinessHourLines(list) {
  const days = [...list]
    .filter((d) => d.dayOfWeek != null || d.day_of_week != null)
    .map((d) => ({
      dayOfWeek: d.dayOfWeek != null ? d.dayOfWeek : d.day_of_week,
      dayNameEn: (d.dayNameEn || d.day_name_en || '').trim(),
      isRest: d.isRest != null ? d.isRest : d.is_rest,
      hoursDisplay: d.hoursDisplay || d.hours_display,
      openTime: d.openTime || d.open_time,
      closeTime: d.closeTime || d.close_time,
      sortOrder: d.sortOrder != null ? d.sortOrder : d.sort_order,
    }))
    .sort((a, b) => {
      const sa = a.sortOrder != null ? a.sortOrder : a.dayOfWeek
      const sb = b.sortOrder != null ? b.sortOrder : b.dayOfWeek
      return sa - sb || a.dayOfWeek - b.dayOfWeek
    })

  if (!days.length) return []

  const lines = []
  let i = 0
  while (i < days.length) {
    const text = dayHoursText(days[i])
    let j = i + 1
    while (j < days.length && dayHoursText(days[j]) === text) {
      j++
    }
    const first = days[i].dayNameEn || `Day${days[i].dayOfWeek}`
    const last = days[j - 1].dayNameEn || first
    const rangeLabel = j - i === 1 ? first : `${first}-${last}`
    lines.push(`${rangeLabel}: ${text}`)
    i = j
  }
  return lines
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.footer {
  background: #0c0b06;
  color: #fff;
  padding: 66px 0 0;
}

.footer-inner {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 56px;
  padding-bottom: 44px;
}

.footer-title {
  font-size: 16px;
  letter-spacing: 0.24em;
  margin-bottom: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
}

.footer-link {
  display: block;
  color: rgba(255, 255, 255, 1);
  text-decoration: none;
  margin-bottom: 9px;
  font-size: 12px;
  line-height: 1.45;
}

.footer-link:hover {
  color: rgba(255, 255, 255, 0.88);
}

.footer-contact-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}

.footer-icon {
  color: rgba(255, 255, 255, 0.45);
  position: relative;
  top:3px;
  flex-shrink: 0;
}

.footer-contact-text {
  color: #fff;
  font-size: 12px;
  line-height: 1.55;
}

.footer-contact-link {
  text-decoration: none;
  color: inherit;
}

.footer-contact-link:hover {
  color: rgba(255, 255, 255, 1);
}

.footer-hours-item {
  color: rgba(255, 255, 255, 1);
  font-size: 12px;
  margin-bottom: 8px;
  line-height: 1.55;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #0f0f0f;
  padding: 24px 0;
}

.footer-bottom-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-brand-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.footer-foot-logo {
  height: 34px;
  width: auto;
  display: block;
  object-fit: contain;
  opacity: 0.18;
}

.footer-brand {
  font-weight: 800;
  letter-spacing: 0.12em;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.15);
}

.footer-brand-sub {
  font-size: 10px;
  letter-spacing: 0.2em;
  color: rgba(255, 255, 255, 0.12);
  text-transform: uppercase;
  margin-top: 2px;
}

.footer-copy {
  color: rgba(255, 255, 255, 0.45);
  font-size: 10px;
  letter-spacing: 0.02em;
}

@media (max-width: 1000px) {
  .footer-inner {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 720px) {
  .container {
    padding: 0 12px;
  }

  .footer-bottom-inner {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .footer-copy {
    text-align: left;
  }
}
</style>

